import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Grid,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Link,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ThemeProviderWrapper from "../components/ThemeProviderWrapper";
import FeedbackButton from "../components/FeedbackButton";
import { ThemeModeProvider } from "../components/ThemeModeContext";
import axiosInstance from "../Services/axiosConfig";

// Configuração de formatos por nacionalidade
const countryPhoneFormats = {
  BR: { code: "+55", format8: "XXXX-XXXX", format9: "XXXXX-XXXX" },
  US: { code: "+1", format: "(XXX) XXX-XXXX" },
  UK: { code: "+44", format: "XXXXX XXXXXX" },
  IN: { code: "+91", format: "XXXXX-XXXXX" },
};

// Função para formatar CPF
const formatCPF = (value) => {
  return value
    .replace(/\D/g, "") // Remove caracteres não numéricos
    .slice(0, 11) // Limita o número de dígitos a 11
    .replace(/(\d{3})(\d)/, "$1.$2") // Adiciona o primeiro ponto
    .replace(/(\d{3})(\d)/, "$1.$2") // Adiciona o segundo ponto
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2"); // Adiciona o traço
};

// Função para formatar telefone com base no país
const formatPhone = (value, country) => {
  const numericValue = value.replace(/\D/g, ""); // Remove caracteres não numéricos

  if (country === "BR") {
    if (numericValue.length <= 10) {
      return numericValue
        .replace(/(\d{2})(\d)/, "($1) $2") // Parênteses no DDD
        .replace(/(\d{4})(\d)/, "$1-$2") // Traço
        .slice(0, 14);
    } else {
      return numericValue
        .replace(/(\d{2})(\d)/, "($1) $2") // Parênteses no DDD
        .replace(/(\d{5})(\d)/, "$1-$2") // Traço
        .slice(0, 15);
    }
  }

  return numericValue;
};

// Função para formatar CEP
const formatZipCode = (value) => {
  return value
    .replace(/\D/g, "") // Remove caracteres não numéricos
    .replace(/(\d{5})(\d{1,3})/, "$1-$2") // Adiciona o traço
    .slice(0, 9); // Limita o tamanho
};

const RegisterPage = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    cpf: "",
    birthday: "",
    phone: "",
    street: "",
    number: "",
    district: "",
    city: "",
    state: "",
    zip_code: "",
    country: "BR",
    address: "",
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const handleChange = (e) => {
    const { name, value } = e.target;

    
    let formattedValue = value;
    if (name === "cpf") {
      formattedValue = formatCPF(value);
    } else if (name === "phone") {
      formattedValue = formatPhone(value, formData.country);
    } else if (name === "zip_code") {
      formattedValue = formatZipCode(value);
    }
    // concatenação de endereço
    const address = `${formData.street}, ${formData.number} - ${formData.district}, ${formData.city} - ${formData.state}, ${formData.zip_code}`;
    setFormData({ ...formData, [name]: formattedValue, address });
  };

  const validateForm = () => {
    if (!agreedToTerms) {
      setErrorMessage("Você precisa concordar com os termos de uso e serviço.");
      return false;
    }
    return true;
  };

  const handleRegister = async () => {
    if (!validateForm()) return;

    const formattedData = {
      ...formData,
      birthday: formData.birthday
        ? new Date(formData.birthday).toISOString().split("T")[0]
        : "",
    };

    setLoading(true);
    setErrorMessage("");

    try {
      const response = await axiosInstance.post("register/", formattedData);
      if (response.data.status === "success") {
        alert("Conta criada com sucesso. Faça login para continuar.");
        window.location.href = "/login";
      } else {
        setErrorMessage(response.data.message);
      }
    } catch (error) {
      setErrorMessage("Ocorreu um erro. Tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeModeProvider>
      <ThemeProviderWrapper>
        <Box
          display="flex"
          minHeight="100vh"
          width="100vw"
          sx={{
            overflow: "hidden",
            position: "relative",
            background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.secondary.light})`,
          }}
        >
          {/* Form Section */}
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            p={4}
            sx={{
              maxWidth: "100vw",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                maxWidth: 500,
                width: "100%",
                bgcolor: "background.paper",
                p: 4,
                borderRadius: 3,
                boxShadow: 4,
              }}
            >
              <Typography variant="h4" align="center" gutterBottom>
                Crie sua conta
              </Typography>

              {errorMessage && (
                <Typography
                  variant="body2"
                  color="error"
                  align="center"
                  gutterBottom
                >
                  {errorMessage}
                </Typography>
              )}

              <TextField
                label="E-mail"
                name="email"
                type="email"
                variant="outlined"
                fullWidth
                required
                margin="normal"
                value={formData.email}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Senha  "
                name="password"
                type="password"
                variant="outlined"
                fullWidth
                required
                margin="normal"
                value={formData.password}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Nome"
                name="name"
                type="text"
                variant="outlined"
                fullWidth
                required
                margin="normal"
                value={formData.name}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    label="CPF"
                    name="cpf"
                    type="text"
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.cpf}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Data de Nascimento"
                    name="birthday"
                    type="date"
                    variant="outlined"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    value={formData.birthday}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    label="País"
                    name="country"
                    select
                    variant="outlined"
                    fullWidth
                    value={formData.country}
                    onChange={(e) =>
                      setFormData({ ...formData, country: e.target.value })
                    }
                  >
                    {Object.entries(countryPhoneFormats).map(
                      ([code, { code: countryCode }]) => (
                        <MenuItem key={code} value={code}>
                          {code} ({countryCode})
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Telefone"
                    name="phone"
                    type="text"
                    variant="outlined"
                    fullWidth
                    required
                    value={formData.phone}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              <Typography variant="subtitle1" gutterBottom>
                Address
              </Typography>
              <TextField
                label="Rua"
                name="street"
                type="text"
                variant="outlined"
                fullWidth
                margin="normal"
                value={formData.street}
                onChange={handleChange}
                sx={{ mb: 2 }}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Número de Residência"
                    name="number"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formData.number}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Bairro/Região"
                    name="district"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formData.district}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Cidade"
                    name="city"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formData.city}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Estado"
                    name="state"
                    type="text"
                    variant="outlined"
                    fullWidth
                    value={formData.state}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
              <TextField
                label="CEP"
                name="zip_code"
                type="text"
                variant="outlined"
                fullWidth
                margin="normal"
                value={formData.zip_code}
                onChange={handleChange}
                sx={{ mt: 2 }}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    Eu concordo com os {" "}
                    <Link href="/terms" target="_self" rel="noopener">
                      Termos de uso e serviço.
                    </Link>
                  </Typography>
                }
              />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleRegister}
                disabled={loading}
                startIcon={
                  loading && <CircularProgress size={20} color="inherit" />
                }
                sx={{
                  textTransform: "uppercase",
                  fontWeight: "bold",
                  boxShadow: 2,
                  "&:hover": {
                    boxShadow: 4,
                    transform: "scale(1.01)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                {loading ? "Registering..." : "Register"}
              </Button>
            </Box>
          </Box>

          <FeedbackButton />

          {/* Keyframes para animações */}
          <style>
            {`
              @keyframes move-1 {
                0% { transform: translate(0, 0); }
                100% { transform: translate(30px, -30px); }
              }
              @keyframes move-2 {
                0% { transform: translate(0, 0); }
                100% { transform: translate(-30px, 30px); }
              }
            `}
          </style>
        </Box>
      </ThemeProviderWrapper>
    </ThemeModeProvider>
  );
};

export default RegisterPage;
